import React, { useState, useEffect } from "react";
import { getLocalizedText, updateLocalizedText } from "../utils/locale";
import { PreviewImage } from "./PreviewImage";
import AutoTextarea from "./AutoTextarea";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "../utils/firebase";

/* ── Style helpers ── */

function mapWeight(w: string | null | undefined): string | undefined {
  if (!w) return undefined;
  if (w === "medium") return "500";
  if (w === "semibold") return "600";
  if (w === "bold") return "700";
  return w;
}

function blockTextStyle(block: any): React.CSSProperties {
  const s = block.style || {};
  const css: React.CSSProperties = {};
  if (s.font_size) css.fontSize = s.font_size;
  if (s.font_weight) css.fontWeight = mapWeight(s.font_weight);
  if (s.font_style) css.fontStyle = s.font_style;
  if (s.color) css.color = s.color;
  return css;
}

function simpleMarkdown(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br>");
}

/* ── MarkdownField: click to edit ── */

const MarkdownField: React.FC<{
  value: string;
  style?: React.CSSProperties;
  onChange: (text: string) => void;
  placeholder?: string;
}> = ({ value, style, onChange, placeholder }) => {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <AutoTextarea
        className="simple__textarea"
        style={style}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        autoFocus
        placeholder={placeholder}
      />
    );
  }
  const html = simpleMarkdown(value);
  return (
    <div
      className="simple__markdown"
      style={{ ...style, cursor: "text" }}
      onClick={() => setEditing(true)}
      dangerouslySetInnerHTML={{ __html: html || `<span class="simple__placeholder">${placeholder || ""}</span>` }}
    />
  );
};

/* ── WorkoutCard: image + video upload, editable title/hint, delete ── */

const WorkoutCard: React.FC<{
  block: any;
  locale: string;
  project: string;
  contentType: string;
  onUpdate: (block: any) => void;
  onDelete?: () => void;
}> = ({ block, locale, project, contentType, onUpdate, onDelete }) => {
  const title = getLocalizedText(block.title, locale);
  const hintText = block.hint?.text ? getLocalizedText(block.hint.text, locale) : "";
  const imagePath = block.image || "";
  const videoPath = block.action?.url || "";

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const imageInput = React.useRef<HTMLInputElement>(null);
  const videoInput = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!imagePath) { setImageUrl(null); return; }
    if (imagePath.startsWith("http")) { setImageUrl(imagePath); return; }
    let cancelled = false;
    getDownloadURL(storageRef(storage, imagePath))
      .then((u) => { if (!cancelled) setImageUrl(u); })
      .catch(() => { if (!cancelled) setImageUrl(null); });
    return () => { cancelled = true; };
  }, [imagePath]);

  useEffect(() => {
    if (!videoPath) { setVideoUrl(null); return; }
    if (videoPath.startsWith("http")) { setVideoUrl(videoPath); return; }
    let cancelled = false;
    getDownloadURL(storageRef(storage, videoPath))
      .then((u) => { if (!cancelled) setVideoUrl(u); })
      .catch(() => { if (!cancelled) setVideoUrl(null); });
    return () => { cancelled = true; };
  }, [videoPath]);

  const uploadFile = async (file: File, subdir: string): Promise<string> => {
    const { uploadBytes } = await import("firebase/storage");
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    const base = file.name.slice(0, file.name.lastIndexOf("."))
      .toLowerCase().replace(/[^a-z0-9_-]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
    const path = `workouts/${subdir}/${base}_${Date.now()}${ext}`;
    await uploadBytes(storageRef(storage, path), file);
    return path;
  };

  const deleteFile = async (path: string) => {
    if (!path || path.startsWith("http")) return;
    try {
      const { deleteObject } = await import("firebase/storage");
      await deleteObject(storageRef(storage, path));
    } catch {}
  };

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      if (imagePath) await deleteFile(imagePath);
      const path = await uploadFile(file, "images");
      onUpdate({ ...block, image: path });
    } catch (err) { console.error("Image upload error:", err); }
    setUploadingImage(false);
  };

  const handleVideoUpload = async (file: File) => {
    setUploadingVideo(true);
    try {
      if (videoPath) await deleteFile(videoPath);
      const path = await uploadFile(file, "videos");
      onUpdate({ ...block, action: { ...block.action, type: "video", url: path } });
    } catch (err) { console.error("Video upload error:", err); }
    setUploadingVideo(false);
  };

  const handleDeleteCard = async () => {
    if (!window.confirm("Delete this card and its media?")) return;
    await deleteFile(imagePath);
    await deleteFile(videoPath);
    onDelete?.();
  };

  return (
    <div className="workout-card">
      <input ref={imageInput} type="file" accept="image/*" onChange={(e) => {
        const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = "";
      }} style={{ display: "none" }} />
      <input ref={videoInput} type="file" accept="video/*" onChange={(e) => {
        const f = e.target.files?.[0]; if (f) handleVideoUpload(f); e.target.value = "";
      }} style={{ display: "none" }} />

      {/* Thumbnail */}
      <div className="workout-card__thumb" onClick={() => imageInput.current?.click()}>
        {uploadingImage ? (
          <div className="workout-card__uploading">Uploading...</div>
        ) : imageUrl ? (
          <img src={imageUrl} alt="" />
        ) : (
          <div className="workout-card__placeholder">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="workout-card__body">
        <AutoTextarea
          className="workout-card__title"
          value={title}
          onChange={(e) => onUpdate(updateLocalizedText(block, "title", locale, e.target.value))}
          placeholder="Exercise name"
          rows={1}
        />
        <input
          className="workout-card__hint"
          value={hintText}
          onChange={(e) => {
            const newHint = { ...block.hint, icon: block.hint?.icon || "clock", text: typeof block.hint?.text === "object" ? { ...block.hint.text, [locale]: e.target.value } : { [locale]: e.target.value } };
            onUpdate({ ...block, hint: newHint });
          }}
          placeholder="e.g. 40 seconds work + 20 seconds rest"
        />
        {/* Video preview / upload */}
        {uploadingVideo ? (
          <div className="workout-card__video-uploading">Uploading video...</div>
        ) : videoUrl ? (
          <div className="workout-card__video-preview">
            <video src={videoUrl} controls preload="metadata" />
            <button className="workout-card__video-replace" onClick={() => videoInput.current?.click()} title="Replace video">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
            </button>
          </div>
        ) : (
          <div className="workout-card__video-add" onClick={() => videoInput.current?.click()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
            <span>Add video</span>
          </div>
        )}
      </div>

      {/* Delete */}
      {onDelete && (
        <button className="workout-card__delete" onClick={handleDeleteCard} title="Delete card">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

/* ── SimpleBlock: recursive block renderer for preview mode ── */

const SimpleBlock: React.FC<{
  block: any;
  locale: string;
  onUpdate: (block: any) => void;
  onDelete?: () => void;
  blockConfig?: any[];
  project: string;
  contentType: string;
  blockPath: number[];
  onAutoSaveImage?: (path: number[], newUrl: string) => void;
}> = ({ block, locale, onUpdate, onDelete, blockConfig, project, contentType, blockPath, onAutoSaveImage }) => {
  const textStyle = blockTextStyle(block);

  const renderChildren = (children: any[], key: string = "content") =>
    children.map((child: any, i: number) => (
      <SimpleBlock key={i} block={child} locale={locale} project={project} contentType={contentType} onUpdate={(b) => {
        const next = [...children]; next[i] = b; onUpdate({ ...block, [key]: next });
      }} blockConfig={blockConfig} blockPath={[...blockPath, i]} onAutoSaveImage={onAutoSaveImage} />
    ));

  if (block.type === "space") return <div style={{ height: block.size || 12 }} />;
  if (block.type === "line") return <hr className="simple__line" />;

  if (block.type === "group") {
    const children = Array.isArray(block.content) ? block.content : [];
    const padding = Array.isArray(block.insets)
      ? `${block.insets[0]}px ${block.insets[1]}px ${block.insets[2]}px ${block.insets[3]}px`
      : "16px";
    return (
      <div className="simple__group" style={{ backgroundColor: block.background_color || undefined, padding }}>
        {renderChildren(children)}
      </div>
    );
  }

  if (block.type === "decorator") {
    const padding = Array.isArray(block.insets)
      ? `${block.insets[0]}px ${block.insets[1]}px ${block.insets[2]}px ${block.insets[3]}px`
      : undefined;
    const child = block.content;
    return (
      <div className="simple__decorator" style={{ backgroundColor: block.color || undefined, padding, borderRadius: block.corner || undefined }}>
        {Array.isArray(child)
          ? renderChildren(child)
          : child
            ? <SimpleBlock block={child} locale={locale} project={project} contentType={contentType} onUpdate={(b) => onUpdate({ ...block, content: b })} blockConfig={blockConfig} blockPath={[...blockPath, 0]} onAutoSaveImage={onAutoSaveImage} />
            : null}
      </div>
    );
  }

  if (block.type === "row") {
    const children = Array.isArray(block.content) ? block.content : [];
    const weights = block.weights;
    return (
      <div className="simple__row">
        {children.map((child: any, i: number) => (
          <div key={i} style={{ flex: weights?.[i] ?? 1 }}>
            <SimpleBlock block={child} locale={locale} project={project} contentType={contentType} onUpdate={(b) => {
              const next = [...children]; next[i] = b; onUpdate({ ...block, content: next });
            }} blockConfig={blockConfig} blockPath={[...blockPath, i]} onAutoSaveImage={onAutoSaveImage} />
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "unit_selector") {
    const metric = Array.isArray(block.metric) ? block.metric : [];
    return <div>{renderChildren(metric, "metric")}</div>;
  }

  if (block.type === "button") {
    const text = getLocalizedText(block.text, locale);
    return (
      <div className="simple__button" style={{ height: block.height || 36, backgroundColor: block.container_color || undefined, ...textStyle }}>
        {text || "Button"}
      </div>
    );
  }

  if (block.type === "title") {
    const text = getLocalizedText(block.text, locale);
    return (
      <AutoTextarea
        className="simple__input simple__input--h1"
        value={text}
        onChange={(e) => onUpdate(updateLocalizedText(block, "text", locale, e.target.value))}
        placeholder="Title"
        rows={1}
      />
    );
  }

  if (block.type === "image") {
    return (
      <PreviewImage
        value={block.url || ""}
        project={project}
        contentType={contentType}
        onChange={(url) => onUpdate({ ...block, url })}
        onAutoSave={(newUrl) => onAutoSaveImage?.(blockPath, newUrl)}
      />
    );
  }

  if (block.type === "text" || block.type === "rich_text") {
    const text = getLocalizedText(block.text, locale);
    return (
      <MarkdownField
        value={text}
        style={textStyle}
        onChange={(t) => onUpdate(updateLocalizedText(block, "text", locale, t))}
        placeholder={block.type === "rich_text" ? "Rich Text" : "Text"}
      />
    );
  }

  if (block.type === "plain_text") {
    const text = getLocalizedText(block.text, locale);
    return (
      <input
        className="simple__input"
        style={textStyle}
        value={text}
        onChange={(e) => onUpdate(updateLocalizedText(block, "text", locale, e.target.value))}
        placeholder="Plain Text"
      />
    );
  }

  if (block.type === "day") {
    const title = getLocalizedText(block.title, locale);
    const meals: any[] = block.meals || [];
    return (
      <div className="simple__day">
        <div className="simple__day-title">{title || "Day"}</div>
        {meals.map((meal: any, i: number) => (
          <div key={i} className="simple__day-meal">
            <span className="simple__day-meal-title">{getLocalizedText(meal.title, locale)}</span>
            <span className="simple__day-meal-desc">{getLocalizedText(meal.description, locale)}</span>
          </div>
        ))}
        {meals.length === 0 && <div className="simple__day-empty">No meals</div>}
      </div>
    );
  }

  if (block.type === "shopping_list") {
    const name = getLocalizedText(block.name, locale);
    const sl = block.action?.shopping_list || {};
    const items: any[] = sl.items || [];
    return (
      <div className="simple__shopping" style={{ borderLeft: `3px solid ${block.color || "#ECCADA"}` }}>
        <div className="simple__shopping-title">{name || "Shopping List"}</div>
        {items.map((item: any, i: number) => (
          <div key={i} className="simple__shopping-item">
            <span className="simple__shopping-emoji">{item.emoji || ""}</span>
            <span className="simple__shopping-name">{getLocalizedText(item.name, locale)}</span>
            <span className="simple__shopping-qty">{getLocalizedText(item.quantity, locale)}</span>
          </div>
        ))}
        {items.length === 0 && <div className="simple__day-empty">No items</div>}
      </div>
    );
  }

  if (block.type === "card") {
    return (
      <WorkoutCard
        block={block}
        locale={locale}
        project={project}
        contentType={contentType}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    );
  }

  return <pre className="simple__json">{JSON.stringify(block, null, 2)}</pre>;
};

export default SimpleBlock;
